import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Shield, CheckCircle, AlertTriangle, FileText, Clock, ArrowRight, MapPin, Building2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

// Static color class maps for Tailwind compilation
const borderTopColorMap = {
  blue: 'border-t-blue-500',
  green: 'border-t-green-500',
  purple: 'border-t-purple-500',
  red: 'border-t-red-500',
};

const textColorMap = {
  blue: 'text-blue-600',
  green: 'text-green-600',
  purple: 'text-purple-600',
  red: 'text-red-600',
};

export default function CompliancePage() {
  const jurisdictions = [
    {
      name: "Scotland",
      tenancyType: "Private Residential Tenancy (PRT)",
      act: "Private Housing (Tenancies) (Scotland) Act 2016",
      features: [
        "Open-ended tenancies (no fixed end date)",
        "18 eviction grounds (Mandatory & Discretionary)",
        "First-tier Tribunal for disputes",
        "Pre-Action Protocol mandatory for rent arrears",
        "Landlord registration required"
      ],
      requirements: [
        { name: "Landlord Registration", mandatory: true },
        { name: "Deposit Protection (30 days)", mandatory: true },
        { name: "Gas Safety Certificate", mandatory: true },
        { name: "EICR (5 yearly)", mandatory: true },
        { name: "EPC", mandatory: true },
        { name: "Interlinked Smoke/CO Alarms", mandatory: true },
        { name: "Legionella Assessment", mandatory: true }
      ],
      color: "blue"
    },
    {
      name: "England",
      tenancyType: "Assured Shorthold Tenancy (AST)",
      act: "Housing Act 1988 (as amended)",
      features: [
        "Fixed term or periodic tenancies",
        "Section 21 (no-fault) & Section 8 (fault) eviction",
        "County Court for possession orders",
        "How to Rent guide required",
        "Right to Rent checks mandatory"
      ],
      requirements: [
        { name: "Deposit Protection (30 days)", mandatory: true },
        { name: "Prescribed Information", mandatory: true },
        { name: "How to Rent Guide", mandatory: true },
        { name: "Gas Safety Certificate", mandatory: true },
        { name: "EICR (5 yearly)", mandatory: true },
        { name: "EPC (min E rating)", mandatory: true },
        { name: "Right to Rent Checks", mandatory: true }
      ],
      color: "red"
    },
    {
      name: "Wales",
      tenancyType: "Standard Occupation Contract",
      act: "Renting Homes (Wales) Act 2016",
      features: [
        "Contract-holder replaces tenant",
        "Written statement within 14 days",
        "Section 173 requires 6 months notice",
        "Fitness for Human Habitation fundamental",
        "Rent Smart Wales registration"
      ],
      requirements: [
        { name: "Rent Smart Wales Registration", mandatory: true },
        { name: "Landlord Licensing", mandatory: true },
        { name: "Written Statement (14 days)", mandatory: true },
        { name: "Fitness for Habitation", mandatory: true },
        { name: "Deposit Protection", mandatory: true },
        { name: "Gas Safety Certificate", mandatory: true },
        { name: "EICR", mandatory: true }
      ],
      color: "green"
    }
  ];

  const services = [
    { name: "Gas Safety Certificate", price: "£75", frequency: "Annual" },
    { name: "EICR", price: "£150", frequency: "Every 5 years" },
    { name: "EPC", price: "£85", frequency: "Every 10 years" },
    { name: "Legionella Assessment", price: "£50", frequency: "Every 2 years" },
    { name: "PAT Testing", price: "£45", frequency: "Annual" },
    { name: "Smoke/CO Alarms", price: "From £35", frequency: "As needed" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/for-landlords" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Landlords
            </Link>
            <Link to="/for-tenants" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Tenants
            </Link>
            <Link to="/properties" className="text-slate-600 hover:text-primary transition-colors font-medium">
              Browse Properties
            </Link>
            <Link to="/login"><Button>Sign In</Button></Link>
          </nav>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/for-landlords" className="text-sm text-slate-700 font-medium">Landlords</Link>
            <Link to="/for-tenants" className="text-sm text-slate-700 font-medium">Tenants</Link>
            <Link to="/login"><Button size="sm">Sign In</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Multi-Jurisdiction Compliance
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            Compliance Across the UK
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Scotland, England, and Wales each have different tenancy laws. LetWise automatically 
            applies the correct legal framework based on your property location.
          </p>
        </div>
      </section>

      {/* Jurisdiction Cards */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-4">Know Your Jurisdiction</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            We automatically detect your property jurisdiction from the postcode and apply 
            the correct requirements. Here is what applies in each region:
          </p>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {jurisdictions.map((jur, idx) => (
              <Card key={idx} className={`p-6 border-t-4 ${borderTopColorMap[jur.color] || 'border-t-blue-500'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className={`w-6 h-6 ${textColorMap[jur.color] || 'text-blue-600'}`} />
                  <h3 className="text-xl font-display font-bold">{jur.name}</h3>
                </div>
                <div className="text-sm font-semibold text-primary mb-2">{jur.tenancyType}</div>
                <div className="text-xs text-slate-500 mb-4">{jur.act}</div>
                
                <h4 className="font-semibold mb-2">Key Features</h4>
                <ul className="space-y-1 mb-6">
                  {jur.features.map((f, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                
                <h4 className="font-semibold mb-2">Compliance Requirements</h4>
                <ul className="space-y-1">
                  {jur.requirements.map((r, i) => (
                    <li key={i} className="text-sm flex items-center gap-2">
                      {r.mandatory ? (
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      <span className={r.mandatory ? 'font-medium' : ''}>{r.name}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-4">Compliance as a Service</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Order certificates directly through the platform. We assign vetted contractors, 
            schedule appointments, and upload certificates automatically.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <Card key={idx} className="p-6 text-center hover:shadow-lg transition-shadow">
                <FileText className="w-10 h-10 mx-auto mb-4 text-primary" />
                <h3 className="text-lg font-bold mb-2">{service.name}</h3>
                <div className="text-3xl font-display font-bold text-primary mb-2">{service.price}</div>
                <div className="text-sm text-slate-500 flex items-center justify-center gap-1">
                  <Clock className="w-4 h-4" />
                  {service.frequency}
                </div>
              </Card>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Card className="inline-block p-6 bg-gradient-to-r from-primary to-primary/80 text-white">
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Automatic expiry reminders</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Vetted local contractors</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Digital certificate storage</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Compliance dashboard</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-display font-bold mb-4">Stay Compliant, Stress-Free</h2>
          <p className="text-xl opacity-90 mb-8">Let us handle the complexity of UK lettings compliance.</p>
          <Link to="/register?role=landlord">
            <Button size="lg" variant="secondary" className="text-primary">
              Get Started <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-display font-bold">LetWise</span>
          </div>
          <div className="border-t border-slate-800 pt-8 text-sm text-slate-500">
            © 2025 LetWise. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
